/**
 * Adapters from API wire-format to the existing UI shapes.
 *
 * The frontend UI types (src/data/types.ts) were designed for the
 * eventual multi-tenant model and the API now returns the same shape.
 * These adapters mostly just convert snake_case → camelCase and fill in
 * fields the Phase 0 API doesn't compute yet (MRR, members, lastActive).
 */
import type { TenantSummary, TenantDetail, WorkspaceSummary } from './types';
import type { Tenant, Workspace } from '@/data/types';

export function tenantSummaryToTenant(t: TenantSummary): Tenant {
  return {
    id: t.id,
    name: t.name,
    slug: t.slug,
    ownerName: t.owner_name,
    ownerEmail: t.owner_email,
    tier: t.tier,
    status: t.status,
    region: 'lon1', // Phase 0 — all instances are in lon1
    mrr: 0, // Phase 0 — no billing for self-managed instances
    workspaces: t.workspace_count,
    members: 0,
    createdAt: t.created_at,
    lastActiveAt: t.created_at,
    supportOwner: t.operator_owner ?? undefined,
    riskFlag: null,
  };
}

export function tenantDetailToTenant(t: TenantDetail): Tenant {
  return {
    id: t.id,
    name: t.name,
    slug: t.slug,
    ownerName: t.owner_name,
    ownerEmail: t.owner_email,
    tier: t.tier,
    status: t.status,
    region: t.workspaces[0]?.region ?? 'lon1',
    mrr: 0,
    workspaces: t.workspaces.length,
    members: 0,
    createdAt: t.created_at,
    lastActiveAt: t.created_at,
    supportOwner: t.operator_owner ?? undefined,
    riskFlag: null,
    notes: t.notes ?? undefined,
  };
}

export function workspaceSummaryToWorkspace(
  ws: WorkspaceSummary,
  tenantId: string,
  tenantTier: Tenant['tier'] = 'business',
): Workspace {
  return {
    id: ws.id,
    tenantId,
    name: ws.name,
    tier: tenantTier,
    status: ws.status,
    region: ws.region,
    version: ws.version,
    latestVersion: ws.latest_version,
    url: ws.url,
    createdAt: ws.created_at,
    lastActiveAt: ws.last_health_check_at ?? ws.created_at,
  };
}
